import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'components/ui/button';
import SimpleHeader from 'components/SimpleHeader';
import { CheckCircle, MapPin, Package, Home, Printer, Calendar, Building, Loader2, AlertCircle } from 'lucide-react';
import { OrderSummaryCard } from 'components/OrderSummaryCard';
import { getOrderByLidioId } from '../services/apiService'; // Yeni servisi import et

// --- Tip Tanımları ---
interface OrderItem {
  name: string;
  quantity: number;
  subtotal: number;
  unitPrice: number;
  freeQty: number;
  designId?: string;
  designName?: string;
  productImageUrl?: string;
}

interface ShippingDetails {
  name: string;
  cift: string;
  email: string;
  eventDate: string;
  phone?: string;
  address: string;
  city: string;
  town: string;
  postalCode: string;
  note?: string;
  organizationName?: string;
}

export default function SiparisOnayPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // URL parametrelerini okumak için

  // State Yönetimi
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Görüntülenecek Veriler
  const [displayData, setDisplayData] = useState<{
    shipping: ShippingDetails | null;
    orderSummary: any | null;
    transactionId: string;
    paymentMethod: string | null;
  }>({
    shipping: null,
    orderSummary: null,
    transactionId: '---',
    paymentMethod: null,
  });

  useEffect(() => {
    const initializePage = async () => {
      // 1. Durum: Navigasyon State'i var (Havale/EFT ile gelindi)
      if (location.state) {
        const state = location.state;
        const rawShipping = state.shippingDetails || {};
        const rawOrder = state.orderDetails || {};

        setDisplayData({
          shipping: mapShippingDetails(rawShipping),
          orderSummary: {
            packageName: rawOrder.packageName,
            packagePrice: rawOrder.packagePrice,
            items: (rawOrder.items || []) as OrderItem[],
            subtotal: rawOrder.subtotal,
            tax: rawOrder.tax || 0,
            shippingFee: rawOrder.shippingFee || 0,
            total: rawOrder.total,
          },
          transactionId: state.orderNumber || state.transactionId || '---',
          paymentMethod: state.paymentMethod || null,
        });
        setIsLoading(false);
        return;
      }

      // 2. Durum: URL Parametresi var (Lidio'dan gelindi)
      const orderIdFromUrl = searchParams.get('orderId');
      
      if (orderIdFromUrl) {
        try {
          setIsLoading(true);
          // Backend'den siparişi çek
          const orderData = await getOrderByLidioId(orderIdFromUrl);

          setDisplayData({
            shipping: mapShippingDetails(orderData.shippingDetails),
            orderSummary: {
              packageName: orderData.packageName,
              packagePrice: orderData.packagePrice,
              items: orderData.items,
              subtotal: orderData.subtotal,
              tax: orderData.tax,
              shippingFee: orderData.shippingFee || 0,
              total: orderData.total,
            },
            transactionId: orderData.lidioOrderId || orderData.transactionId || orderIdFromUrl,
            paymentMethod: orderData.paymentMethod || null,
          });
        } catch (err) {
          console.error("Sipariş verisi çekilemedi:", err);
          setError("Sipariş detayları alınamadı. Lütfen e-postanızı kontrol edin.");
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // 3. Durum: Ne state var ne URL parametresi -> Anasayfaya at
      navigate('/');
    };

    initializePage();
  }, [location.state, searchParams, navigate]);

  const trackedRef = useRef(false);

  // Meta Pixel: Satın alma tamamlandığında Purchase ve custom "sipariş" eventlerini gönder
  useEffect(() => {
    if (
      !isLoading &&
      displayData.orderSummary?.total &&
      displayData.transactionId &&
      displayData.transactionId !== '---' &&
      typeof window !== 'undefined' &&
      (window as any).fbq &&
      !trackedRef.current
    ) {
      const orderId = displayData.transactionId;
      const storageKey = 'anIkareTrackedOrders';
      
      try {
        const trackedOrders = JSON.parse(localStorage.getItem(storageKey) || '[]');
        if (trackedOrders.includes(orderId)) {
          console.log('🟡 Bu sipariş daha önce Pixel tarafından takip edilmiş, tekrar gönderilmedi:', orderId);
          return;
        }
        
        // Eşsiz sipariş takibi için işaretle ve kaydet
        trackedRef.current = true;
        trackedOrders.push(orderId);
        localStorage.setItem(storageKey, JSON.stringify(trackedOrders));
      } catch (e) {
        console.error('LocalStorage erişim hatası:', e);
        trackedRef.current = true;
      }

      // Standart Purchase
      (window as any).fbq('track', 'Purchase', {
        value: displayData.orderSummary.total,
        currency: 'TRY',
        content_name: displayData.orderSummary.packageName || 'Anora Paketi',
        content_type: 'product',
        order_id: orderId,
      }, {
        eventID: orderId
      });

      // Özel "sipariş" event'i
      (window as any).fbq('trackCustom', 'sipariş', {
        value: displayData.orderSummary.total,
        currency: 'TRY',
        content_name: displayData.orderSummary.packageName || 'Anora Paketi',
        content_type: 'product',
        order_id: orderId,
      }, {
        eventID: orderId + '_custom'
      });

      console.log('🔵 Meta Pixel: Purchase ve "sipariş" eventleri başarıyla gönderildi:', orderId);
    }
  }, [isLoading, displayData]);

  // Yardımcı: Veri formatlama fonksiyonu
  const mapShippingDetails = (data: any): ShippingDetails => ({
    name: data.name || data.fullName || '',
    cift: data.cift || '',
    email: data.email || '',
    eventDate: data.eventDate || 'Belirtilmedi',
    phone: data.phone,
    address: data.address || '',
    city: data.city || '',
    town: data.town || data.district || '',
    postalCode: data.postalCode || '',
    note: data.note,
    organizationName: data.organizationName,
  });


  // --- RENDER ---

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#e45078] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Sipariş detayları yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md w-full border border-red-100">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Bir Sorun Oluştu</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-[#e45078] w-full">Anasayfaya Dön</Button>
        </div>
      </div>
    );
  }

  // Veri yoksa (Type safety için)
  if (!displayData.shipping || !displayData.orderSummary) return null;

  const { shipping, orderSummary, transactionId, paymentMethod } = displayData;

  // Havale Bilgileri
  const BANK_TRANSFER_INFO = {
    bankName: 'Garanti BBVA',
    accountName: 'ANORA FOREVER YAZILIM VE REKLAM ORGANİZASYON LİMİTED ŞİRKETİ',
    iban: 'TR73 0006 2000 7210 0006 2913 93',
  };
  const isWireTransfer = paymentMethod === 'bank_transfer';

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col print-bg-white">
      {/* HEADER: ekranda görünsün, yazdırmada gizlensin */}
      <div className="no-print">
        <SimpleHeader />
      </div>

      <main className="flex-grow container mx-auto max-w-4xl px-4 py-12 print-area pt-24">
        {/* --- 1. Başarı Mesajı ve İkon --- */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center mb-8 animate-in fade-in zoom-in duration-500">
          <div className="flex justify-center mb-4">
            <CheckCircle className="w-20 h-20 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Siparişiniz Alındı!</h1>
          <p className="text-gray-600 text-lg">
            Teşekkürler <span className="font-semibold text-gray-900">{shipping.name}</span>,
            siparişiniz başarıyla oluşturuldu.
          </p>
          <div className="mt-6 inline-flex flex-col items-center justify-center bg-rose-50 border border-rose-100 px-6 py-3 rounded-xl">
            <span className="text-rose-800 text-sm font-medium uppercase tracking-wider">
              Sipariş Numaranız
            </span>
            <span className="text-rose-900 text-lg md:text-2xl font-extrabold tracking-widest mt-1 select-all break-all">
              {transactionId}
            </span>
          </div>

          {/* Havale Bilgileri: Sadece EFT/Havale seçilmişse göster */}
          {isWireTransfer && (
            <div className="mt-6 mx-auto max-w-md text-left bg-amber-50 border border-amber-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">🏦</span>
                <h2 className="font-bold text-amber-900 text-base">Havale / EFT Bilgileri</h2>
              </div>
              <ul className="space-y-1.5 text-sm text-amber-900">
                <li><span className="font-semibold">Banka:</span> {BANK_TRANSFER_INFO.bankName}</li>
                <li><span className="font-semibold">Hesap Adı:</span> {BANK_TRANSFER_INFO.accountName}</li>
                <li>
                  <span className="font-semibold">IBAN:</span>{' '}
                  <span className="font-mono tracking-wide font-bold">{BANK_TRANSFER_INFO.iban}</span>
                </li>
                <li className="text-xs text-amber-800 mt-1">Açıklamaya sipariş numaranızı (<strong>{transactionId}</strong>) eklemeyi unutmayınız.</li>
              </ul>
              <div className="mt-4 p-3 bg-amber-100 rounded-lg border border-amber-300">
                <p className="text-sm font-semibold text-amber-900 text-center">
                  ⏳ Ödemeniz tamamlandıktan sonra siparişiniz hazırlanmaya başlayacaktır.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* --- 2. Sol Kolon: Sipariş Özeti --- */}
          <div className="space-y-6 print-avoid-break">
            <div className="flex items-center gap-2 text-gray-800 font-semibold text-xl border-b pb-2">
              <Package className="text-[#e45078]" />
              <h2>Sipariş Özeti</h2>
            </div>

            <OrderSummaryCard orderDetails={orderSummary} hideEditButton={true} />
          </div>

          {/* --- 3. Sağ Kolon: Teslimat Bilgileri --- */}
          <div className="space-y-6 print-avoid-break">
            <div className="flex items-center gap-2 text-gray-800 font-semibold text-xl border-b pb-2">
              <MapPin className="text-[#e45078]" />
              <h2>Teslimat Bilgileri</h2>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
              {/* Alıcı & Organizasyon */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Alıcı</h3>
                <p className="text-gray-900 font-medium mt-1 text-lg">{shipping.name}</p>
                {shipping.organizationName && (
                  <div className="flex items-center gap-1 text-gray-600 mt-1 text-sm">
                    <Building className="w-3 h-3" />
                    <span>{shipping.organizationName}</span>
                  </div>
                )}
              </div>

              {/* Etkinlik Tarihi */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Etkinlik Tarihi
                </h3>
                <div className="flex items-center gap-2 mt-1 text-gray-900">
                  <Calendar className="w-4 h-4 text-rose-500" />
                  <span className="font-medium">{shipping.eventDate}</span>
                </div>
              </div>

              {/* İletişim */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">İletişim</h3>
                <p className="text-gray-900 mt-1">{shipping.phone}</p>
                <p className="text-gray-900">{shipping.email}</p>
              </div>

              {/* Adres */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Teslimat Adresi
                </h3>
                <p className="text-gray-900 mt-1 leading-relaxed">
                  {shipping.address}
                  <br />
                  <span className="font-medium">
                    {shipping.town} / {shipping.city}
                  </span>
                  <br />
                  <span className="text-gray-500 text-sm">{shipping.postalCode}</span>
                </p>
              </div>

              {/* Varsa Not */}
              {shipping.note && (
                <div className="bg-yellow-50 p-3 rounded-md border border-yellow-100">
                  <h3 className="text-xs font-bold text-yellow-700 uppercase tracking-wider">
                    Sipariş Notu
                  </h3>
                  <p className="text-yellow-800 text-sm mt-1 italic">
                    "{shipping.note}"
                  </p>
                </div>
              )}
            </div>

            {/* --- 4. Aksiyon Butonları --- */}
            <div className="bg-rose-50 rounded-xl p-6 border border-rose-100 flex flex-col gap-4 no-print">
              <div>
                <h3 className="font-bold text-gray-800">Bilgilendirme</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Sipariş detaylarınız <strong>{shipping.email}</strong> adresine gönderildi.
                </p>
              </div>

              <div className="flex gap-3 mt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
                  onClick={() => window.print()}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Yazdır
                </Button>
                <Button
                  className="flex-1 bg-[#e45078] hover:bg-rose-600 text-white"
                  onClick={() => navigate('/')}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Anasayfa
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}